import type { Opportunity } from "@/model/DataModel"
import { OpportunityField } from "@/graphql"
import { formatQuantity } from "@/utils/format"
import { formatOpportunityArea, formatOpportunityPricePerArea } from "@/utils/opportunityUnitFields"
import { AreaUnit } from "@/utils/unitSystem"

export function snakeToCamel(s: string): string {
	return s.replace(/(^|_)([a-z])/g, (_, _sep, c: string) => c.toUpperCase())
}

function formatPrice(value: number | null | undefined): string {
	if (!value) return ""
	return `${formatQuantity(Math.round(value))} $`
}

function formatParcels(lots: number[] | null | undefined): string {
	if (!lots?.length) return ""
	return lots.join(", ")
}

export enum ColumnKey {
	Address = "address",
	Area = "area",
	AssetType = "asset-type",
	Broker = "broker",
	BuildableArea = "buildable-area",
	Category = "category",
	City = "city",
	Deal = "deal",
	Map = "map",
	Notes = "notes",
	Parcels = "parcels",
	Price = "price",
	PricePerBuildableSqft = "price-per-buildable-sqft",
	PricePerSqft = "price-per-sqft",
	Priority = "priority",
	Stage = "stage",
	Status = "status",
	Subcategory = "subcategory",
	// details-panel-only sections (ExpandedRowCompact), not real Opportunity fields — never rendered as table columns
	NotesInput = "notes-input",
	Links = "links-data",
	Location = "location",
}

export const DETAILS_PANEL_ONLY_KEYS: ColumnKey[] = [ColumnKey.NotesInput, ColumnKey.Links, ColumnKey.Location]

export const FIELDLESS_COLUMN_KEYS: ColumnKey[] = [ColumnKey.Notes, ColumnKey.NotesInput]

// fields that can never move to the details panel: Address is pinned, Status/Priority/Stage are dropdowns the details grid can't render
export const TABLE_ONLY_KEYS: ColumnKey[] = [ColumnKey.Address, ColumnKey.Status, ColumnKey.Priority, ColumnKey.Stage]

// every other field can be freely moved between the main table and ExpandedRowCompact's details grid
export const MOVABLE_KEYS: ColumnKey[] = [
	ColumnKey.Area,
	ColumnKey.AssetType,
	ColumnKey.Broker,
	ColumnKey.BuildableArea,
	ColumnKey.Category,
	ColumnKey.City,
	ColumnKey.Notes,
	ColumnKey.Parcels,
	ColumnKey.Price,
	ColumnKey.PricePerBuildableSqft,
	ColumnKey.PricePerSqft,
	ColumnKey.Subcategory,
]

// of those, the ones that start out in the details panel group by default (the rest start in the main table)
const DEFAULT_DETAIL_KEYS: ColumnKey[] = [
	ColumnKey.AssetType,
	ColumnKey.Broker,
	ColumnKey.BuildableArea,
	ColumnKey.Category,
	ColumnKey.City,
	ColumnKey.Notes,
	ColumnKey.Parcels,
	ColumnKey.PricePerSqft,
	ColumnKey.Subcategory,
]

const COLUMN_SELECT: Record<ColumnKey, OpportunityField[]> = {
	[ColumnKey.Address]: [OpportunityField.LotAddress],
	[ColumnKey.Area]: [OpportunityField.AnalysisLandArea],
	[ColumnKey.AssetType]: [OpportunityField.AssetType],
	[ColumnKey.Broker]: [
		OpportunityField.BrokerFirstName,
		OpportunityField.BrokerLastName,
		OpportunityField.CollaborativeBrokerNames,
	],
	[ColumnKey.BuildableArea]: [OpportunityField.AnalysisBuildableArea],
	[ColumnKey.Category]: [OpportunityField.LandCategoryNew],
	[ColumnKey.City]: [OpportunityField.LotCity],
	[ColumnKey.Deal]: [OpportunityField.HubspotRecordId],
	[ColumnKey.Map]: [OpportunityField.Latitude, OpportunityField.Longitude],
	[ColumnKey.Notes]: [],
	[ColumnKey.Parcels]: [OpportunityField.LotNumbersFull],
	[ColumnKey.Price]: [OpportunityField.OpportunityAskingPrice],
	[ColumnKey.PricePerBuildableSqft]: [OpportunityField.AnalysisBuildableAreaPricePerSqft],
	[ColumnKey.PricePerSqft]: [OpportunityField.AnalysisLandAreaPricePerSqft],
	[ColumnKey.Priority]: [OpportunityField.Priority],
	[ColumnKey.Stage]: [OpportunityField.StageLabel],
	[ColumnKey.Status]: [OpportunityField.Status],
	[ColumnKey.Subcategory]: [OpportunityField.LandSubcategory],
	[ColumnKey.NotesInput]: [],
	[ColumnKey.Links]: [
		OpportunityField.DataRoomCloudFile,
		OpportunityField.OpportunityWebpageUrl,
		OpportunityField.ModellingLink,
	],
	[ColumnKey.Location]: [
		OpportunityField.LotAddress,
		OpportunityField.LotCity,
		OpportunityField.LotBorough,
		OpportunityField.LotRegion,
		OpportunityField.Latitude,
		OpportunityField.Longitude,
	],
}

type OpportunityColumnConfig = {
	key: string
	visible: boolean
	group?: string
}

export function defaultOpportunityColumnGroup(key: string): string {
	return DEFAULT_DETAIL_KEYS.includes(key as ColumnKey) ? "details-panel" : "table"
}

function opportunityColumnFields(key: string): OpportunityField[] {
	return COLUMN_SELECT[key as ColumnKey] ?? []
}

export function selectedOpportunityColumnFields(columns: { key: string; visible: boolean }[]): OpportunityField[] {
	return [...new Set(columns.filter(column => column.visible).flatMap(column => opportunityColumnFields(column.key)))]
}

export function opportunityColumnsFromSelectedFields(
	fields: OpportunityField[],
	availableKeys: string[]
): OpportunityColumnConfig[] {
	const selectedFields = new Set(fields)
	const visible = availableKeys
		.filter(key => {
			const mappedFields = opportunityColumnFields(key)
			return mappedFields.length > 0 && mappedFields.every(field => selectedFields.has(field))
		})
		.map(key => ({
			key,
			visible: true,
			group: defaultOpportunityColumnGroup(key),
			index: Math.min(...opportunityColumnFields(key).map(field => fields.indexOf(field))),
		}))
		.sort((left, right) => left.index - right.index)
		.map(({ key, visible, group }) => ({ key, visible, group }))

	const visibleKeys = new Set(visible.map(column => column.key))
	const hidden = availableKeys
		.filter(key => !visibleKeys.has(key))
		.map(key => ({
			key,
			visible: false,
			group: defaultOpportunityColumnGroup(key),
		}))

	return [...visible, ...hidden]
}

type TranslateFn = (key: string) => string

export function opportunityColumnLabel(
	key: string,
	t: TranslateFn,
	areaUnitSymbol: string,
	pricePerAreaUnitSymbol: string
): string {
	switch (key) {
		case ColumnKey.Area:
		case ColumnKey.BuildableArea:
			return `${t(`opportunities-table.columns.${key}`)} (${areaUnitSymbol})`
		case ColumnKey.PricePerSqft:
		case ColumnKey.PricePerBuildableSqft:
			return `${pricePerAreaUnitSymbol} ${t(`opportunities-table.columns.${key}`)}`
		default:
			return t(`opportunities-table.columns.${key}`)
	}
}

export function formatMovableColumnValue(
	key: ColumnKey,
	item: Opportunity,
	t: TranslateFn,
	areaUnit = AreaUnit.SquareFeet
): string {
	switch (key) {
		case ColumnKey.AssetType:
			return item.assetType?.map(c => t("property.assetType." + c)).join(", ") ?? ""
		case ColumnKey.Category:
			return item.landCategoryNew?.map(c => t("property.landCategory." + c)).join(", ") ?? ""
		case ColumnKey.Subcategory:
			return item.landSubcategory?.map(c => t("property.landSubcategory." + snakeToCamel(c))).join(", ") ?? ""
		case ColumnKey.Broker:
			return [
				[item.broker_first_name, item.broker_last_name].filter(Boolean).join(" "),
				...(item.collaborative_broker_names ?? []),
			]
				.filter(Boolean)
				.join(", ")
		case ColumnKey.City:
			return item.city ?? ""
		case ColumnKey.Area:
			return formatOpportunityArea(item.superficy, areaUnit)
		case ColumnKey.BuildableArea:
			return formatOpportunityArea(item.constructible, areaUnit)
		case ColumnKey.Price:
			return formatPrice(item.price)
		case ColumnKey.PricePerBuildableSqft:
			return formatOpportunityPricePerArea(item.pricePerConstructibleSqft, areaUnit)
		case ColumnKey.PricePerSqft:
			return formatOpportunityPricePerArea(item.pricePerSqft, areaUnit)
		case ColumnKey.Parcels:
			return formatParcels(item.lot_numbers_full)
		default:
			return ""
	}
}
